/**
 * V3.1o — Panel "Notes & Rapports" pour les dossiers CEE / MaPrimeAdapt.
 *
 * - Liste tous les items `action in {'note','rapport'}` de la timeline.
 * - Permet d'ajouter une note (text libre + choix note/rapport).
 * - Affiche date + auteur (nom OU email) + source (crm / whatsapp_ai).
 *
 * Utilise les endpoints :
 *   GET  /api/cee/dossiers/{id}/notes
 *   POST /api/cee/dossiers/{id}/notes  { text, kind: "note"|"rapport" }
 */
import { useEffect, useState } from 'react';
import {
  NotebookPen, Loader2, Bot, User, FileText, StickyNote, RefreshCw, Send,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';


function fmtDateTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}


export default function DossierNotesPanel({ dossierId, amo }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [text, setText] = useState('');
  const [kind, setKind] = useState('note');

  // Envoi d'une note / rapport à l'AMO
  const [sendOpen, setSendOpen] = useState(false);
  const [sendNote, setSendNote] = useState(null);  // { at, note, action }
  const [sendMessage, setSendMessage] = useState('');
  const [sendEmailOverride, setSendEmailOverride] = useState('');
  const [sending, setSending] = useState(false);

  const openSendDialog = (n) => {
    setSendNote(n);
    setSendMessage('');
    setSendEmailOverride('');
    setSendOpen(true);
  };

  const handleSendToAmo = async () => {
    if (!sendNote) return;
    setSending(true);
    try {
      const r = await api.post(
        `/cee/dossiers/${dossierId}/notes/send-to-amo`,
        {
          note_at: sendNote.at,
          custom_message: sendMessage.trim() || null,
          override_email: sendEmailOverride.trim() || null,
        },
      );
      toast.success(`Envoyé à ${r.data.sent_to} (${r.data.amo_nom})`,
                     { duration: 6000 });
      setSendOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi", { duration: 8000 });
    } finally { setSending(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/cee/dossiers/${dossierId}/notes`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error('Impossible de charger les notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (dossierId) load(); }, [dossierId]);

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await api.post(`/cee/dossiers/${dossierId}/notes`,
                      { text: trimmed, kind });
      toast.success(kind === 'rapport' ? 'Rapport ajouté.' : 'Note ajoutée.');
      setText('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur ajout note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="dossier-notes-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <NotebookPen className="h-4 w-4 text-slate-600" />
            Notes &amp; rapports
            <Badge variant="outline" className="ml-1 bg-slate-50">
              {items.length}
            </Badge>
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Formulaire d'ajout */}
        <div className="space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ex. Appel du 05/02 : le client attend un retour sur la couleur du carrelage…"
            rows={3}
            className="resize-y"
            data-testid="dossier-note-text"
          />
          <div className="flex items-center justify-between gap-2">
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="h-9 w-40 text-xs"
                              data-testid="dossier-note-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="note">
                  <span className="flex items-center gap-2">
                    <StickyNote className="h-3.5 w-3.5" /> Note
                  </span>
                </SelectItem>
                <SelectItem value="rapport">
                  <span className="flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5" /> Rapport
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={submit}
              disabled={!text.trim() || saving}
              className="bg-[#1C49A6] hover:bg-[#173B85] text-white"
              data-testid="dossier-note-submit"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Ajouter
            </Button>
          </div>
        </div>

        {/* Liste */}
        <div className="space-y-3">
          {loading && items.length === 0 ? (
            <div className="text-center text-slate-500 py-6">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
              Chargement…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center text-slate-400 py-6 text-sm">
              Aucune note ni rapport pour l'instant.
            </div>
          ) : (
            items.map((n, idx) => {
              const isWA = (n.source || '') === 'whatsapp_ai';
              const Icon = n.action === 'rapport' ? FileText : StickyNote;
              return (
                <div key={`${n.at}-${idx}`}
                     className={`border rounded-lg p-3 ${
                       isWA ? 'border-emerald-200 bg-emerald-50/40'
                            : 'border-slate-200 bg-white'
                     }`}
                     data-testid={`dossier-note-item-${idx}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Icon className={`h-3.5 w-3.5 ${
                        n.action === 'rapport' ? 'text-indigo-600' : 'text-slate-500'
                      }`} />
                      <Badge
                        variant="outline"
                        className={n.action === 'rapport'
                          ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                          : 'bg-slate-50 border-slate-200 text-slate-700'}
                      >
                        {n.action === 'rapport' ? 'Rapport' : 'Note'}
                      </Badge>
                      <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                        {isWA
                          ? <><Bot className="h-3 w-3 text-emerald-600" /> Secrétaire IA WhatsApp</>
                          : <><User className="h-3 w-3" /> {n.user_name || n.user}</>
                        }
                      </span>
                    </div>
                    <span className="text-xs text-slate-400 shrink-0">
                      {fmtDateTime(n.at)}
                    </span>
                  </div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">
                    {n.note}
                  </div>
                  {!isWA && amo && (amo.amo_id || amo.amo_email) && (
                    <div className="mt-2 pt-2 border-t border-slate-100 flex justify-end">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => openSendDialog(n)}
                        className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid={`dossier-note-send-amo-${idx}`}
                        title={`Envoyer à l'AMO ${amo.amo_nom || ''}`}
                      >
                        <Send className="h-3 w-3 mr-1.5" />
                        Envoyer à l'AMO
                      </Button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </CardContent>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-lg" data-testid="send-note-to-amo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-amber-600" />
              Envoyer {sendNote?.action === 'rapport' ? 'ce rapport' : 'cette note'} à l'AMO
            </DialogTitle>
            <DialogDescription>
              Destinataire : <b>{amo?.amo_nom || '—'}</b>
              {amo?.amo_email && (
                <> · <span className="font-mono text-xs">{amo.amo_email}</span></>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {sendNote && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                  {sendNote.action === 'rapport' ? 'Rapport' : 'Note'} du {fmtDateTime(sendNote.at)}
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap line-clamp-4">
                  {sendNote.note}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="send-override-email" className="text-sm">
                Email destinataire (optionnel)
              </Label>
              <Input
                id="send-override-email"
                type="email"
                value={sendEmailOverride}
                onChange={(e) => setSendEmailOverride(e.target.value)}
                placeholder={amo?.amo_email || "autre@email.fr"}
                data-testid="send-override-email"
              />
              <p className="text-xs text-slate-500">
                Laissez vide pour utiliser l'email de l'AMO rattaché au dossier.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="send-custom-message" className="text-sm">
                Message personnalisé (optionnel)
              </Label>
              <Textarea
                id="send-custom-message"
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                placeholder="Ex: Bonjour, voici le rapport de visite. Pouvez-vous valider les préconisations avant lundi ?"
                rows={4}
                maxLength={2000}
                data-testid="send-custom-message"
                className="resize-none"
              />
              <p className="text-xs text-slate-500">
                Ce message apparaîtra en évidence dans l'email ({sendMessage.length}/2000)
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSendOpen(false)}
                    disabled={sending}>
              Annuler
            </Button>
            <Button onClick={handleSendToAmo} disabled={sending}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="send-note-submit">
              {sending
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi…</>
                : <><Send className="w-4 h-4 mr-2" /> Envoyer à l'AMO</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
